import React, { useEffect, useState } from "react";
import { Alert } from "react-bootstrap";
import EnrollmentService from "../../../_services/EnrollmentService";
import CollapsibleSection from "./CollapsibleSection";
import ExpandableTxTable, { TxColumn } from "./ExpandableTxTable";
import { fmtAmount } from "./format";

interface RefundSectionProps {
  enrollmentId: number;
}

/**
 * Refund Details — mirrors the legacy Java "REFUND DETAILS" grid. One wide,
 * advice-driven table: Advice Date, Advice Number, Advice Amount, Excess
 * Amount, Refund Date, Refund Voucher No., Details, Refund Amount, Status.
 * Each row expands to instrument/bank/reason detail.
 */
const RefundSection: React.FC<RefundSectionProps> = ({ enrollmentId }) => {
  const [refunds, setRefunds] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    EnrollmentService.getRefunds(enrollmentId)
      .then((res: any) => {
        if (res.data?.success) setRefunds(res.data.data.refunds || []);
        else setError(res.data?.message || "Failed to load refunds");
      })
      .catch((err: any) =>
        setError(
          err.response?.data?.message || err.message || "Failed to load refunds",
        ),
      )
      .finally(() => setLoading(false));
  }, [enrollmentId]);

  // Hide only on a genuinely empty (successful) result; never hide a failure.
  if (!loading && !error && refunds.length === 0) return null;

  const statusStyle = (status: string): React.CSSProperties => {
    const map: Record<string, { bg: string; fg: string }> = {
      Refunded: { bg: "var(--status-success-bg)", fg: "var(--status-success)" },
      Approved: { bg: "var(--status-success-bg)", fg: "var(--status-success)" },
      Open: { bg: "var(--status-warning-bg)", fg: "var(--status-warning)" },
      InProgress: { bg: "var(--status-info-bg)", fg: "var(--status-info)" },
      Rejected: { bg: "var(--status-danger-bg)", fg: "var(--status-danger)" },
    };
    const c = map[status] || { bg: "var(--surface-page)", fg: "var(--text-muted)" };
    return {
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: 999,
      fontSize: 10,
      fontWeight: 700,
      background: c.bg,
      color: c.fg,
    };
  };

  const columns: TxColumn[] = [
    { header: "Advice Date", cell: (r) => r.adviceDate || "--", nowrap: true },
    { header: "Advice Number", cell: (r) => r.refundAdviceNumber || "--" },
    {
      header: "Advice Amount",
      align: "right",
      cell: (r) => (r.adviceAmount != null ? fmtAmount(r.adviceAmount) : "--"),
    },
    {
      header: "Excess Amount",
      align: "right",
      cell: (r) =>
        r.utilizedExcessAmount != null ? fmtAmount(r.utilizedExcessAmount) : "--",
    },
    { header: "Refund Date", cell: (r) => r.refundDate || "--", nowrap: true },
    { header: "Refund Voucher No.", cell: (r) => r.refundVoucherNumber || "--" },
    {
      header: "Details",
      cell: (r) => {
        const parts = [
          r.instrumentTypeName && r.instrumentTypeName !== "--"
            ? r.instrumentTypeName
            : "",
          r.bankBranchName,
        ].filter(Boolean);
        return parts.length ? parts.join(", ") : "--";
      },
    },
    {
      header: "Refund Amount",
      align: "right",
      cell: (r) => (r.refundedAmount != null ? fmtAmount(r.refundedAmount) : "--"),
    },
    {
      header: "Status",
      cell: (r) => <span style={statusStyle(r.status)}>{r.status}</span>,
    },
  ];

  return (
    <CollapsibleSection
      title="Refund Details"
      count={refunds.length}
      defaultOpen={false}
    >
      {loading ? (
        <div className="ev-loading">
          <i className="fa fa-spinner fa-spin" /> Loading...
        </div>
      ) : error ? (
        <Alert variant="danger" style={{ fontSize: 12, marginBottom: 0 }}>
          {error}
        </Alert>
      ) : (
        <ExpandableTxTable
          rows={refunds}
          columns={columns}
          rowKey={(r, i) =>
            r.refundAdviceId ? `a${r.refundAdviceId}` : `p${i}`
          }
          hasDetails={() => true}
          renderDetails={(r) => (
            <div className="ev-detail-meta" style={{ borderTop: "none", marginTop: 0, paddingTop: 0 }}>
              <div className="ev-detail-line">
                <span className="ev-detail-head">
                  Instrument: <b>{r.instrumentTypeName || "--"}</b>
                  {r.instrumentDate && r.instrumentDate !== "--"
                    ? ` · Date: ${r.instrumentDate}`
                    : ""}
                  {r.instrumentIdentifier
                    ? ` · Ref: ${r.instrumentIdentifier}`
                    : ""}
                </span>
              </div>
              {r.bankBranchName && (
                <div className="ev-detail-line">
                  <span className="ev-detail-head">Bank Branch: <b>{r.bankBranchName}</b></span>
                </div>
              )}
              <div className="ev-detail-line">
                <span className="ev-detail-head">
                  Reason: <b>{r.reason || "--"}</b> · Remarks: <b>{r.remarks || "--"}</b>
                </span>
              </div>
              {r.actedByName && (
                <div className="ev-detail-line">
                  <span className="ev-detail-head">
                    Acted By: <b>{r.actedByName}</b>
                    {r.actedDate ? ` (${r.actedDate})` : ""}
                  </span>
                </div>
              )}
            </div>
          )}
        />
      )}
    </CollapsibleSection>
  );
};

export default RefundSection;
